<template>
  <div class="kpi-scores-wrapper">
    <div class="kpi-scores">
      <HorizontalLoading v-if="kpis.isLoading" />
      <div v-for="kpiWithScore in kpiWithScores"
           :key="kpiWithScore.key"
           :class="{ active: selectedKpi === kpiWithScore.key }"
           class="kpi-scores__box"
           @click="selectKpi(kpiWithScore.key)">
        <div class="label">
          {{ normalizeString(kpiWithScore.key) }}
        </div>
        <div class="value">
          {{ kpiWithScore.value.toFixed(kpiWithScore.props.float_digits) }}{{ kpiWithScore.props.symbol }}
        </div>
      </div>
    </div>

    <el-dropdown class="select-kpi"
                 trigger="click"
                 @command="changeSelectedKpi">
      <span class="el-dropdown-link">
        <span class="dropdown-link-wrapper">
          <span class="score-title">{{ normalizeString(selectedKpi) }}</span>
          <span class="score">{{ selectedForDropdown.value.toFixed(selectedForDropdown.props.float_digits) }}{{ selectedForDropdown.props.symbol }}</span>
        </span>

        <i class="el-icon-arrow-down el-icon--right" />
      </span>
      <el-dropdown-menu slot="dropdown"
                        size="small"
                        class="select-kpi-dropdown">
        <el-dropdown-item v-for="(kpiWithScore, index) in kpiWithScores"
                          :key="index"
                          :class="{'selected': kpiWithScore.key === selectedKpi}"
                          :command="kpiWithScore">
          <span>{{ normalizeString(kpiWithScore.key) }}</span>
        </el-dropdown-item>
      </el-dropdown-menu>
    </el-dropdown>
  </div>
</template>

<script>
import { isEqual, sortBy } from 'lodash';
import { getTemplateProps } from '../../executive-view/utils';
import { fetchExecutiveViewTrendAndAverages } from '../../executive-view-summary/api';

export default {
  props: {
    periods: {
      type: Object,
      required: true
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    collectedTags: {
      type: Array,
      default() {
        return [];
      }
    },
    auditorAndStoreFilters: {
      type: Object,
      required: true
    },
    teamGroupFilter: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      selectedKpi: '',
      kpis: {
        hasError: false,
        isLoaded: false,
        isLoading: false,
        list: []
      },
      selectedForDropdown: {
        key: '',
        props: {},
        value: 0
      }
    };
  },
  computed: {
    collectedTagsIds() {
      return this.collectedTags.reduce((acc, item) => {
        acc.push(...item.tags.map(tag => tag.id));

        return acc;
      }, []);
    },
    kpiWithScores() {
      const list = this.kpis.list.map(item => ({
        ...item,
        props: getTemplateProps(item.key)
      }));

      return sortBy(list, 'props.position');
    }
  },
  watch: {
    collectedTagsIds(oldVal, newVal) {
      if (!isEqual(oldVal, newVal)) {
        this.fetchKpis();
      }
    },
    periods() {
      this.fetchKpis();
    },
    auditorAndStoreFilters() {
      this.fetchKpis();
    },
    teamGroupFilter() {
      this.fetchKpis();
    }
  },
  mounted() {
    this.fetchKpis();
  },
  methods: {
    selectKpi(kpi) {
      this.selectedKpi = kpi;
      sessionStorage.setItem('selectedKpi', kpi);
      this.emit();
    },
    changeSelectedKpi(value) {
      this.selectedForDropdown = value;
      this.selectKpi(value.key);
    },
    emit() {
      this.$emit('input', this.selectedKpi);
    },
    async fetchKpis() {
      const {
        projectId, periods, auditorAndStoreFilters, teamGroupFilter
      } = this;

      this.kpis.isLoading = true;

      try {
        const { data: { data } } = await fetchExecutiveViewTrendAndAverages(projectId, {
          filter: {
            project: projectId,
            stores: {
              tags: this.collectedTagsIds
            },
            auditor_ids: auditorAndStoreFilters.auditor_ids,
            store_ids: auditorAndStoreFilters.store_ids,
            team_group_id: teamGroupFilter.id,
            start_date_with_time: this.convertToServerTimeZone(periods.current.start_time_without_utc),
            end_date_with_time: this.convertToServerTimeZone(periods.current.end_time_without_utc)
          }
        });

        this.kpis.list = data.kpis;
        this.kpis.isLoaded = true;
        const [first] = this.kpiWithScores;
        const selectedKpiStatus = sessionStorage.getItem('selectedKpi');
        this.selectedForDropdown = first;

        if (selectedKpiStatus) {
          this.selectedKpi = selectedKpiStatus;
        } else {
          this.selectedKpi = first.key;
        }
        this.emit();
      } catch (e) {
        this.kpis.hasError = true;
      }

      this.kpis.isLoading = false;
    }
  }
};
</script>

<style lang="scss">
.kpi-scores-wrapper {
  box-sizing: border-box;

  @include breakpoint(md-s-max) {
    padding: 0 10px;
  }
}

.kpi-scores {
  box-sizing: border-box;
  display: grid;
  grid-template-rows: auto auto;
  grid-template-columns: auto auto auto auto;
  width: 99%;
  max-height: 220px;
  overflow-y: scroll;
  color: $darkGrey;
  background-color: #f1f1f1;

  &__box {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    height: 65px;
    padding: 10px 20px;
    margin: 5px 5px 5px 10px;
    cursor: pointer;
    background-color: $white;
    border-radius: 3px;

    &.active {
      color: white;
      background-color: $andreaBlue;
      border-right-color: transparent;
      border-radius: 6px;
      transition: background-color 0.3s;
    }

    .label {
      width: 150px;
      font-size: 18px;
      font-weight: 600;
    }

    .value {
      font-size: 38px;
      font-weight: 600;
    }
  }
}

.kpi-scores::-webkit-scrollbar {
  width: 12px !important;
}

.kpi-scores::-webkit-scrollbar-thumb {
  background: $darkSkyBlue !important;
  -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
  box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.5);
}

.select-kpi {
  display: none;

  @include breakpoint(lg-max) {
    position: absolute;
    top: -118px;
    right: 20px;
    z-index: 2;
    display: inherit;
  }

  @include breakpoint(md-s-max) {
    position: initial;
    display: inherit;
    width: 96%;
    padding: 0 10px;
  }

  .el-input {
    &__inner {
      font-size: 16px;
      font-weight: 600;
      color: $white;
      background-color: #4a90e2;
      border-radius: 70px;
    }

    .el-select__caret {
      font-weight: bold;
      color: $white;
    }
  }

  &.el-dropdown {
    width: 100%;
    max-width: 218px;
    padding: 5px 30px 5px 15px;
    background-color: #4090e2;
    border-radius: 130px;

    @include breakpoint(md-s-max) {
      width: 91%;
      max-width: unset;
      padding: 5px 15px 5px 20px;
    }

    .el-dropdown-link {
      display: flex;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      color: $white;
      cursor: pointer;

      .dropdown-link-wrapper {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        .score-title {
          width: 259px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .score-title,
        .score {
          font-size: 16px;
        }
      }

      i {
        font-size: 16px;
        font-weight: bold;
      }
    }
  }
}

.select-kpi-dropdown {
  width: 100%;
  max-width: 324px;
  max-height: 187px;
  padding: 15px 0 15px 15px;
  overflow-y: scroll;
  border-radius: 7px;

  .el-dropdown-menu__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    font-weight: 700;

    &.selected {
      color: #4a90e2;
    }
  }

  &::-webkit-scrollbar {
    width: 4px;
    -webkit-appearance: none;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    min-height: 8px;
    max-height: 8px;
    background: #d6dee2;
    border-radius: 8px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #bdc9d1;
  }
}

@include breakpoint(lg-max) {
  .kpi-scores {
    display: none;
    grid-template-columns: unset;
  }
}

@include breakpoint(md-s-max) {
  .kpi-scores {
    display: none;
    grid-template-columns: unset;
  }
}
</style>
